import React from 'react';
import i18next from 'i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import Breakpoints from 'lib/Breakpoints';

import Related from 'components/Related';
import GiftGuide from 'components/GiftGuide';

const ASPECT_1X1 = 'ASPECT_1X1';
const ASPECT_2X1 = 'ASPECT_2X1';

const mapStateToProps = (state) => {
  const taxonomy = get(state, 'article.content[0].taxonomy');
  const articleID = get(state, 'article.content[0].id');
  return {
    taxonomy,
    articleID,
  };
};

class Recommended extends React.Component {
  static propTypes = {
    additionalClasses: PropTypes.string,
    isGiftGuide: PropTypes.bool,
    isNBCNewsCommerce: PropTypes.bool,
    isBetterNewsCommerce: PropTypes.bool,
    isRightRail: PropTypes.bool,
    isTrending: PropTypes.bool,
    mobile: PropTypes.bool,
    taxonomy: PropTypes.shape({
      primarySection: PropTypes.shape({
        id: PropTypes.string,
      }),
      primaryTopic: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
    articleID: PropTypes.string,
  };

  static defaultProps = {
    additionalClasses: '',
    isGiftGuide: false,
    isNBCNewsCommerce: false,
    isBetterNewsCommerce: false,
    isRightRail: false,
    isTrending: false,
    mobile: true,
    taxonomy: {},
    articleID: '',
  };

  constructor(props) {
    super(props);
    this.state = {
      formattedRecommendationItems: [],
    };
  }

  componentDidMount() {
    const formattedRecommendationItems = this.getFormattedItems();

    this.setState({ formattedRecommendationItems });
  }

  /**
   * Filter + formatter function that skips any unwanted recommended items. Items are translated
   * into a format expected by RelatedItem component.
   */
  getFormattedItems() {
    const { articleID, taxonomy } = this.props;
    const taxonomyPrimaryTopicName = get(taxonomy, ['primaryTopic', 'name']);
    const targetTaxonomyPrimary = this.getTargetTaxonomyName(taxonomyPrimaryTopicName);
    const items = get(taxonomy, [targetTaxonomyPrimary, 'content', 'items'], []);
    const itemLimit = this.getLimit();

    const formatRecommendedItems = (formattedItems, item) => {
      const { type, id } = item;

      if (this.shouldSkipItem(
        item,
        formattedItems,
        itemLimit,
        articleID,
        taxonomyPrimaryTopicName,
      )) return formattedItems;

      const alternateTeaseImages = get(item, ['alternateTeaseImages'], []);

      const thumbnail1x1 = alternateTeaseImages.find(
        ({ aspectRatio }) => aspectRatio === ASPECT_1X1,
      );
      const thumbnail2x1 = alternateTeaseImages.find(
        ({ aspectRatio }) => aspectRatio === ASPECT_2X1,
      );

      const primaryThumbnail = get(thumbnail1x1, ['image', 'url', 'primary'], null);
      const secondaryThumbnail = get(thumbnail2x1, ['image', 'url', 'primary'], null);
      const defaultThumbnail = get(item, ['primaryImage', 'url', 'primary']);

      const thumbnail = primaryThumbnail || secondaryThumbnail || defaultThumbnail;

      const unibrow = {
        url: get(item, ['unibrow', 'url']),
        text: get(item, ['unibrow', 'text']),
      };
      const title = get(item, ['headline', 'tease']);
      const url = get(item, ['url', 'primary']);
      const formattedItem = {
        id,
        url,
        type,
        title,
        unibrow,
        thumbnail,
      };

      formattedItems.push(formattedItem);

      return formattedItems;
    };

    return items.reduce(formatRecommendedItems, []);
  }

  /**
   * Logic added for BENTO-15064. The taxonomy 'Gift Guide' is also an article type, so we want to
   * avoid showing duplicate recommended content when this component is rendered on the right
   * rail and the article footer (only affects articles of Taxonomy type 'Gift Guide')
   */
  getTargetTaxonomyName = (taxonomyPrimaryTopicName) => {
    const { isRightRail } = this.props;

    return isRightRail && taxonomyPrimaryTopicName === 'Gift Guides'
      ? 'primarySection'
      : 'primaryTopic';
  }


  getLimit = () => {
    const {
      isGiftGuide, isTrending, isNBCNewsCommerce, isBetterNewsCommerce,
    } = this.props;

    if (isNBCNewsCommerce || isTrending || isGiftGuide || isBetterNewsCommerce) {
      return 4;
    }

    return Breakpoints.isS() ? 1 : 2;
  }

  /**
   * Certain article types do not get gift guide components, and small screens do not have the room.
   */
  shouldShowRelatedContent = () => {
    const {
      mobile,
      isTrending,
      isGiftGuide,
      isNBCNewsCommerce,
      isBetterNewsCommerce,
    } = this.props;
    if (isTrending || isGiftGuide || isNBCNewsCommerce || isBetterNewsCommerce) {
      return true;
    }
    return (mobile && Breakpoints.isSorM()) || (!mobile && Breakpoints.isLorXL());
  }

  shouldSkipItem = (item, formattedItems, itemLimit, articleID, taxonomyPrimaryTopicName) => {
    const { isRightRail, isGiftGuide } = this.props;
    const { taxonomy: itemTaxonomy, id } = item;
    const relatedItemPrimaryTopicName = get(itemTaxonomy, ['primaryTopic', 'name']);
    const itemIsSkippable = formattedItems.length >= itemLimit
      || !id // A recommended article without an ID is likely invalid, and can be skipped
      || articleID === id;

    if (itemIsSkippable) {
      return true;
    }

    if (isRightRail && taxonomyPrimaryTopicName === 'Gift Guides') {
      return taxonomyPrimaryTopicName === relatedItemPrimaryTopicName;
    }

    if (isGiftGuide) {
      return relatedItemPrimaryTopicName !== 'Gift Guides';
    }

    return taxonomyPrimaryTopicName !== relatedItemPrimaryTopicName;
  }

  render() {
    const {
      additionalClasses,
      isGiftGuide,
      isNBCNewsCommerce,
      isBetterNewsCommerce,
      isRightRail,
      isTrending,
    } = this.props;
    const { formattedRecommendationItems = [] } = this.state;

    // No results, return early
    if (!formattedRecommendationItems.length) {
      return null;
    }

    const title = isTrending ? i18next.t('Trending') : i18next.t('Recommended');
    const id = title.toLowerCase();

    let linkTrackingID;
    if (isTrending || isNBCNewsCommerce) {
      linkTrackingID = 'recommended_shop';
    } else {
      linkTrackingID = id;
    }

    if (isGiftGuide) {
      return (
        <GiftGuide
          items={formattedRecommendationItems}
          data-test="gift-guide"
        />
      );
    }

    if (this.shouldShowRelatedContent()) {
      return (
        <Related
          additionalClasses={additionalClasses}
          elementId={id}
          isNBCNewsCommerce={isNBCNewsCommerce}
          isBetterNewsCommerce={isBetterNewsCommerce}
          isRightRail={isRightRail}
          isTrending={isTrending}
          items={formattedRecommendationItems}
          linkTracking={linkTrackingID}
          title={title}
        />
      );
    }

    return null;
  }
}

export default connect(mapStateToProps)(Recommended);
